import { Component, ViewEncapsulation, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import { Price } from '@app/entities/price';
import { AppFacade } from '@app/app.facade';
import { BookingFacade } from '@booking/booking.facade';
import { ANIMATIONS, TIME_TO_SLIDE } from '@app/constants/animations.constants';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { AirValidationSector } from '@app/entities/air-validation.response';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { VehicleValidateResponse } from '@autos/entities/vehicle-validate.response';
import { FlightResultsCabinFacade } from '@flight-results/flight-results-cabin.facade';
import { AvoService } from '@avo/services/avo.service';
import { totalNights } from '@ux/mappers/guest.mapper';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { ActivitiesValidateResponse, ActivityFareBookParamsVM } from '@activities/entities/activities-booking';
import { SummaryCardOption, SummaryCardType } from '@design-system/components/organisms/summary-card/entities/summary-card.types';
import { HeaderWidgetType } from '@design-system/components/templates/header/entities/header.types';
import { getFareAttributes } from '@activities/mappers/fare-attributes.mapper';

@Component({
  selector: 'app-mobile-booking-detail',
  templateUrl: './mobile-booking-detail.component.html',
  styleUrls: ['./mobile-booking-detail.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MobileBookingDetailComponent implements OnInit, OnDestroy {

  public passengerArrangement: PassengerArrangement[];
  public price: Price;
  public flightClass: FlightClass;
  public airValidationSector: AirValidationSector[];
  public animation: string = ANIMATIONS.SLIDE_IN_RIGHT;
  public provider: string;
  public travelType: TravelBookingType;
  public isHotelDetail: boolean = false;
  public hotelValidation: HotelValidationResponse;
  public isAutoDetail: boolean = false;
  public autoValidation: VehicleValidateResponse;
  public sectorSelected: number;
  public policiesType: SearchBy = SearchBy.FLIGHTS;
  public isInternational: boolean;
  public productReference: number;
  public capacity: number;
  public activitiesValidation: ActivitiesValidateResponse;
  public isActivitiesDetail = false;
  public faresVm: ActivityFareBookParamsVM[] = [];
  public activityName: string;

  protected AvoDispatch = new AvoDispatchOrchestrator();

  public readonly summaryCardType = SummaryCardType;

  public readonly headerWidgetType = HeaderWidgetType;

  constructor(
    private readonly facade: AppFacade,
    private readonly router: Router,
    private readonly domManipulateService: DomManipulateService,
    private readonly flightsCabinFacade: FlightResultsCabinFacade,
    private readonly bookingFacade: BookingFacade,
    private readonly avoService: AvoService,
    private readonly transloco: TranslocoService,
  ) {
    const extras = this.router.getCurrentNavigation()?.extras;
    if (extras && extras.state) {
      if (extras.state.isAutoDetail) {
        this.isAutoDetail = extras.state.isAutoDetail;
        this.autoValidation = extras.state.autoValidation;
        this.policiesType = SearchBy.AUTOS;
      } else if (extras.state.isHotelDetail) {
        this.isHotelDetail = extras.state.isHotelDetail;
        this.hotelValidation = extras.state.hotelValidation;
        this.policiesType = SearchBy.HOTELS;
        this.capacity = extras.state.capacity;
      } else if (extras.state.isActivitiesDetail) {
        this.isActivitiesDetail = extras.state.isActivitiesDetail;
        this.activitiesValidation = extras.state.activitiesValidation;
        this.faresVm = extras.state.faresVm;
        this.activityName = extras.state.activityName;
        this.policiesType = SearchBy.ACTIVITIES;
      } else {
        this.passengerArrangement = extras.state.passengerArrangement;
        this.price = extras.state.price;
        this.flightClass = extras.state.flightClass;
        this.airValidationSector = extras.state.airValidationSector;
        this.provider = extras.state.provider;
        this.travelType = extras.state.travelType;
        this.sectorSelected = extras.state.sectorSelected;
        this.policiesType = SearchBy.FLIGHTS;
        this.isInternational = extras.state.isInternational;
        this.productReference = extras.state.productReference;
      }
    }
  }


  ngOnInit(): void {
    this.domManipulateService.setBodyScroll(false);
    this.domManipulateService.setBookingScroll(false);
  }

  ngOnDestroy(): void {
    this.domManipulateService.setBodyScroll(true);
    this.domManipulateService.setBookingScroll(true);
  }

  get guestsPerRoomState$(): Observable<Room[]> {
    return this.bookingFacade.guestsPerRoomState$;
  }

  get isLoadingFlightCabin$(): Observable<boolean> {
    return this.flightsCabinFacade.isLoadingFlightCabin$;
  }

  get conversionRate$(): Observable<number> {
    return this.bookingFacade.exchangeRate$;
  }

  get nights(): number {
    return this.hotelValidation?.HotelReservation ?
      totalNights(this.hotelValidation?.HotelReservation.Checkout, this.hotelValidation?.HotelReservation.Checkin) : 0;
  }

  get pricePerNights(): number {
    return this.hotelValidation?.Amount?.Total / this.nights;
  }

  get summaryOptions(): SummaryCardOption[] {
    const options = getFareAttributes(this.activitiesValidation, this.faresVm);
    return options.map(option => ({
      ...option,
      Title: this.transloco.translate(option.Title),
      Description: this.transloco.translate(option.Description),
    }));
  }

  public goBack(): void {
    this.animation = ANIMATIONS.SLIDE_OUT_RIGHT;
    setTimeout(() => this.facade.navigateToLastRoute(), TIME_TO_SLIDE);
    this.domManipulateService.setBodyScroll(true);
    this.domManipulateService.setBookingScroll(true);
  }
}
